import { useEffect, useState } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { webApiClient } from '../lib/fi-api/apiUtils';

export type PromoDiscountType = 'dollars' | 'percent';

export interface PromoCodeDetailsItem {
  sku: string;
  platform: string;
  renewalMonths: number;
  priceInCents: number;
  discountInCents: number;
  couponCode: string;
  discountType: PromoDiscountType;
}

export interface PromoCodeDetailsSetupFee {
  sku: string;
  platform: string;
  priceInCents: number;
  discountInCents: number;
  couponCode: string;
  discountType: PromoDiscountType;
}

export interface PromoCodeDetails {
  items: PromoCodeDetailsItem[];
  setupFees: PromoCodeDetailsSetupFee[];
}

const EMPTY_DETAILS: PromoCodeDetails = { items: [], setupFees: [] };

const promiseCache = new Map<string, Promise<PromoCodeDetails>>();

async function fetchCodeDetails(code: string, currency: string): Promise<PromoCodeDetails> {
  try {
    const response = await webApiClient.get<PromoCodeDetails>('/api/promotions/code-details', {
      params: { code, currency },
    });
    return response.data;
  } catch {
    // Evict on failure so a transient network error doesn't poison the rest of the session.
    promiseCache.delete(`${code}:${currency}`);
    return EMPTY_DETAILS;
  }
}

function getPromoCodeDetails(code: string, currency: string): Promise<PromoCodeDetails> {
  const key = `${code}:${currency}`;
  let promise = promiseCache.get(key);
  if (!promise) {
    promise = fetchCodeDetails(code, currency);
    promiseCache.set(key, promise);
  }
  return promise;
}

/**
 * Fetches per-plan discount details for a promo code in the user's currency
 * from `/api/promotions/code-details`. Setup-fee discounts are returned in
 * a separate `setupFees` array keyed by platform; callers sum the matching
 * setup fee into the plan discount to render accurate "Save $X with promo"
 * badges.
 *
 * Concurrent hook instances with the same `(code, currency)` share a single
 * in-flight request via a module-level Promise cache; the cache also serves
 * later mounts so repeat navigations skip the network entirely.
 *
 * Returns `undefined` while the fetch is in flight or when `code` is
 * undefined, and `{ items: [], setupFees: [] }` when the endpoint reports
 * nothing to show (unknown / expired code, unsupported discount type,
 * network error, etc.).
 */
export function usePromoCodeDetails(code: string | undefined): PromoCodeDetails | undefined {
  const { currency } = useLocale();
  const [details, setDetails] = useState<PromoCodeDetails | undefined>(undefined);

  useEffect(() => {
    if (!code) {
      setDetails(undefined);
      return;
    }

    const activeCode = code;
    let cancelled = false;
    setDetails(undefined);

    async function loadDetails() {
      const data = await getPromoCodeDetails(activeCode, currency);
      if (!cancelled) {
        setDetails(data);
      }
    }
    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [code, currency]);

  return details;
}
